import React, {
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import './shoppingCart.less';
import ShoppingCartHeader from './widgets/ShoppingCartHeader';
import ShoppingCartEmpty from './widgets/ShoppingCartEmpty';
import { isEmpty } from 'lodash';
import { AiFillCloseCircle } from 'react-icons/ai';
import DiscountCode from './widgets/DiscountCode';
import ShoppingCartSubsidize from './widgets/ShoppingCartSubsidize';
import { Button, message, Modal, Spin, Tooltip } from 'antd';
import useHandleCheckout from './hooks/useHandleCheckout';
import { isStaging } from '../../utils/environment';
import { MainProductType, Product } from './interfaces';
import { setWholeCart, TransferProduct } from './ShoppingCartSliceV2';
import type { ShoppingCartV2 as ShoppingCartV2State } from './ShoppingCartSliceV2';
import AgentActions from './agentActions/AgentActions';
import ShoppingCartActivity from './products/ShoppingCartActivity/ShoppingCartActivity';
import ShoppingCartRoom from './products/ShoppingCartRoom/ShoppingCartRoom';
import ShoppingCartPackage from './products/ShoppingCartPackage/ShoppingCartPackage';
import ShoppingCartHotel from './products/ShoppingCartHotel/ShoppingCartHotel';
import ShoppingCartFlight from './products/ShoppingCartFlight/ShoppingCartFlight';
import ShoppingCartFlightPackage from './products/ShoppingCartFlightPackage/ShoppingCartFlightPackage';
import useInitializeCurrentCustomer from './hooks/useInitializeCurrentCustomer';
import { MdWarning } from 'react-icons/md';
import { isIncompleteDynamicPackage } from './shoppingCartUtils';
import useUpdateCommissionNeeded from './hooks/useIsUpdateCommissionNeeded';
import { useNavigate } from 'react-router-dom';
import CartNotValid from './widgets/CartNotValid';
import AddUserModal from './agentActions/AddUserModal/AddUserModal';
import ShoppingCartOneWayFlight from './products/ShoppingCartOneWayFlight/ShoppingCartOneWayFlight';
import { packageLink } from '@skygroup/shared/utils/B2CLinks';
import useCssVariables from '../../hooks/useCssVariables';
import ButtonWithIcon from '../../components/Common/ButtonWithIcon';
import { FaRegSave } from 'react-icons/fa';
import { updateCommission } from '../Checkout/CheckoutApis';
import useCleanUpLocalStorage from './hooks/useCleanUpLocalStorage';
import ShoppingCartAttraction from './products/ShoppingCartAttraction/ShoppingCartAttraction';
import ShoppingCartEvent from './products/ShoppingCartEvent/ShoppingCartEvent';
import ShoppingCartTransfer from './products/ShoppingCartTransfer/ShoppingCartTransfer';
import { useEventTicketPaxMismatchWarning } from './hooks/useAddToCartWarning';
import ShoppingCartInstallmentsInterest from './products/ShoppingCartInstallmentsInterest/ShoppingCartInstallmentsInterest';

interface EventTicketPaxValidationResult {
  isValid: boolean;
  totalEventTickets: number;
  totalFlightPax: number;
  hasEvents: boolean;
}

const getFlightPaxForDynamicPackage = (dynamicPackage: Product): number => {
  const subProducts = dynamicPackage?.subProducts || [];

  const flightsContainer = subProducts.find(
    (sp) => sp.type === MainProductType.Flights
  );

  if (flightsContainer) {
    const pax = (flightsContainer as any).product_data?.pax || {};
    const adult = Number(pax.adult || 0);
    const child = Number(pax.child || 0);
    return adult + child;
  }

  const flightProducts = subProducts.filter(
    (sp) =>
      sp.type === MainProductType.Flight ||
      sp.type === MainProductType.oneWayFlight
  );

  if (flightProducts.length) {
    let total = 0;

    flightProducts.forEach((flight) => {
      const pax = (flight as any).product_data?.pax || {};
      const adult = Number(pax.adult || 0);
      const child = Number(pax.child || 0);

      if (adult || child) {
        total = Math.max(total, adult + child);
      } else if (typeof flight.product_amount === 'number') {
        total = Math.max(total, flight.product_amount || 0);
      } else if (Array.isArray(flight.related_guest_indexes)) {
        total = Math.max(total, flight.related_guest_indexes.length);
      }
    });

    return total;
  }

  return 0;
};

const getEventTicketsForDynamicPackage = (
  dynamicPackage: Product
): { totalTickets: number; hasEvents: boolean } => {
  const subProducts = dynamicPackage?.subProducts || [];
  let totalTickets = 0;
  let hasEventsWithTickets = false;

  subProducts.forEach((subProduct) => {
    if (subProduct.type === MainProductType.Event && subProduct.subProducts) {
      subProduct.subProducts.forEach((ticketSubProduct) => {
        if (ticketSubProduct.type === MainProductType.EventTicket) {
          hasEventsWithTickets = true;

          if (typeof ticketSubProduct.product_amount === 'number') {
            totalTickets += ticketSubProduct.product_amount || 0;
          } else if (Array.isArray(ticketSubProduct.related_guest_indexes)) {
            totalTickets += ticketSubProduct.related_guest_indexes.length;
          }
        }
      });
    }
  });

  return { totalTickets, hasEvents: hasEventsWithTickets };
};

const validateDynamicPackageEventTicketsVsFlightPax = (
  _shoppingCart: ShoppingCartV2State | null | undefined,
  dynamicPackage: Product
): EventTicketPaxValidationResult => {
  const { totalTickets, hasEvents } =
    getEventTicketsForDynamicPackage(dynamicPackage);

  if (!hasEvents) {
    return {
      isValid: true,
      totalEventTickets: 0,
      totalFlightPax: 0,
      hasEvents: false,
    };
  }

  const totalFlightPax = getFlightPaxForDynamicPackage(dynamicPackage);

  return {
    isValid: totalTickets === totalFlightPax,
    totalEventTickets: totalTickets,
    totalFlightPax,
    hasEvents: true,
  };
};

interface Props {
  pageType: string;
  open?: boolean;
  closeFromHeader?: () => void;
  showDiscounCodeOrIntegrationInfo?: boolean;
  isMobileView?: boolean;
  isLocalInventory?: any;
}

const ShoppingCartV2Component = ({
  pageType,
  open,
  closeFromHeader,
  showDiscounCodeOrIntegrationInfo = false,
  isMobileView = false,
  isLocalInventory,
}: Props) => {
  const isCheckoutPage = pageType === 'checkout' || pageType === 'manage-order';
  const isMangeOrderPage = pageType === 'manage-order';
  const [shoppingCartOpen, setShoppingCartOpen] = useState(false);
  const {
    shoppingCartTranslations: t,
    wallet: t_wallet,
  } = useSelector((data: RootState) => data.config.translations);

  const { appliedSubsidize } = useSelector(
    (state: RootState) => state.shoppingCart
  );
  const { ShoppingCartV2: shoppingCartV2, agent_commission } = useSelector(
    (state: RootState) => state.shoppingCartV2
  );


  const [addUserModal, setAddUserModal] = useState({
    open: false,
    customer_secret: '',
  });
  const isUpdateCommissionNeeded = useUpdateCommissionNeeded();

  useCleanUpLocalStorage();

  const { agentInfo, config } = useSelector((data: RootState) => data.config);

  useEffect(() => {
    if (!!shoppingCartV2?.orderId && !isEmpty(shoppingCartV2?.orderId)) {
      setShoppingCartOpen(true);
    }
  }, [shoppingCartV2]);

  const navigate = useNavigate();
  const checkout = useHandleCheckout(isCheckoutPage);
  const eventTicketPaxMismatchWarning = useEventTicketPaxMismatchWarning();

  const handleCheckout = () => {
    const { orderId, isSaved, orderSecret, products } = (shoppingCartV2 ||
      {}) as ShoppingCartV2State;

    // Handle case when no order exists
    if (!orderId) {
      message.warning('Cannot checkout: No order ID found');
      return;
    }

    // Order exists but not saved or missing secret - run validations, then proceed to checkout
    if (!isSaved || !orderSecret) {
      // Check for incomplete dynamic packages
      const incompleteDPs = (products || []).filter(
        (p) =>
          p.type === MainProductType.DynamicPackage &&
          isIncompleteDynamicPackage(p)
      );
      if (incompleteDPs.length > 0) {
        message.warning(
          t.dynamicPackageIncompleteCheckout ||
            'Cannot proceed to checkout: One or more dynamic packages are incomplete. Please add the missing hotel+flight or event tickets.'
        );
        return;
      }

      // Run dynamic package event ticket vs pax validation
      const dynamicPackagesWithEvents = (products || []).filter(
        (product: Product) => {
          if (product?.type !== MainProductType.DynamicPackage) {
            return false;
          }

          const subProducts = product.subProducts || [];

          return subProducts.some(
            (subProduct) =>
              subProduct.type === MainProductType.Event &&
              (subProduct.subProducts || []).some(
                (ticketSubProduct) =>
                  ticketSubProduct.type === MainProductType.EventTicket
              )
          );
        }
      );

      for (const dynamicPackage of dynamicPackagesWithEvents) {
        const validationResult = validateDynamicPackageEventTicketsVsFlightPax(
          shoppingCartV2 as ShoppingCartV2State,
          dynamicPackage
        );

        if (!validationResult.isValid) {
          eventTicketPaxMismatchWarning();
          return;
        }
      }

      checkout();
      return;
    }

    // Order is saved with secret - navigate to order management page
    navigate(`order/manage?id=${orderSecret}`);
  };

  const { settings } = useSelector((state: RootState) => state.shoppingCart);

  const showCloseButton = useMemo(
    () =>
      (open || shoppingCartOpen) &&
      pageType !== 'checkout' &&
      pageType !== 'manage-order',
    [open, pageType, shoppingCartOpen]
  );

  const freePackage = useMemo(() => {
    if (pageType === 'manage-order' && settings?.price_option_type === 'free')
      return true;
    if (settings?.price_option_type === 'free') return true;
    return false;
  }, [settings, pageType]);

  const className = settings?.flight_only ? 'flight-only' : '';

  const memoizedIsUpdateNeeded = useMemo(() => {
    return isUpdateCommissionNeeded();
  }, [isUpdateCommissionNeeded]);

  const generateCssVariables = useCssVariables();

  const dispatch = useDispatch();
  useEffect(() => {
    if (!!shoppingCartV2?.orderId) {
      dispatch(
        setWholeCart({
          ...shoppingCartV2,
          isSaved: !isUpdateCommissionNeeded(),
        })
      );
    }
  }, [memoizedIsUpdateNeeded]);

  useInitializeCurrentCustomer();

  const [isUpdateCommissionLoading, setIsUpdateCommissionLoading] =
    useState(false);
  const handleUpdateCommission = async (e: any) => {
    e.stopPropagation();
    await updateCommission(
      {
        ...shoppingCartV2,
        agent_commission: {
          ...agent_commission,
          is_commission_per_order: 1,
        },
      },
      shoppingCartV2?.orderSecret ?? '',
      setIsUpdateCommissionLoading
    );
    dispatch(
      setWholeCart({
        ...shoppingCartV2,
        agent_commission: {
          ...agent_commission,
        },
      })
    );
  };

  const showProductPrice = !(
    shoppingCartV2?.customerInfo?.agency_id && !agentInfo?.agentMode
  );
  // const showProductPrice = !shoppingCartV2?.customerInfo?.agency_id || agentInfo?.agentMode;

  if (isStaging()) {
    console.log('123 shoppingCartV2', shoppingCartV2);
  }

  // this function is render each product ( * also the sub products)
  const ProductRenderer = useCallback(
    ({
      mainProductType,
      product,
      identifier,
      isTopLevel = false,
      topLevelProductId,
      isSameDates = false,
      transfer_type,
      parentCurrencySign,
      parentTotalPrice,
    }: {
      mainProductType?: MainProductType;
      product: Product;
      identifier?: string;
      isTopLevel?: boolean;
      topLevelProductId?: string | number;
      isSameDates?: boolean;
      transfer_type?: string;
      parentCurrencySign?: string;
      parentTotalPrice?: number;
    }) => {
      const topLevelProductData = shoppingCartV2?.products?.find(
        (prd: Product) =>
          prd.identifier === topLevelProductId || prd.id === topLevelProductId
      );

      const parentPackageTotalPrice =
        mainProductType === MainProductType.DynamicPackage
          ? parentTotalPrice ?? topLevelProductData?.total_price
          : undefined;
      const resolvedParentCurrencySign =
        mainProductType === MainProductType.DynamicPackage
          ? topLevelProductData?.base_currency_sign ?? parentCurrencySign
          : parentCurrencySign;

      switch (product?.type) {
        case MainProductType.Activity:
          return (
            <ShoppingCartActivity
              freePackage={freePackage}
              isCheckoutPage={isCheckoutPage}
              product={product}
              mainProductType={mainProductType}
              showProductPrice={showProductPrice}
            />
          );
        case MainProductType.Attraction:
          return (
            <ShoppingCartAttraction
              product={product}
              identifier={identifier as string}
              isCheckoutPage={isCheckoutPage}
              mainProductType={mainProductType}
              showProductPrice={showProductPrice}
            />
          );
        case MainProductType.Event:
          return (
            <ShoppingCartEvent
              product={product}
              identifier={identifier as string}
              isCheckoutPage={isCheckoutPage}
              isMangeOrderPage={isMangeOrderPage}
              mainProductType={mainProductType}
              showProductPrice={showProductPrice}
            />
          );
        case MainProductType.EventTicket:
          // EventTicket is a sub-product, should be handled by parent Event component
          return null;
        case MainProductType.Flights:
          return (
            <ShoppingCartFlightPackage
              product={product}
              isCheckoutPage={isCheckoutPage}
              mainProductType={mainProductType}
              showProductPrice={showProductPrice}
              // if Bundle_flight is not a top level product, no need to show "X" close:
              hideCloseIcon={isCheckoutPage || !isTopLevel}
              isTopLevel={isTopLevel}
            />
          );
        case MainProductType.oneWayFlights:
          return (
            <ShoppingCartOneWayFlight
              product={product}
              isCheckoutPage={isCheckoutPage}
              showProductPrice={showProductPrice}
              mainProductType={mainProductType}
              hideCloseIcon={isCheckoutPage || !isTopLevel}
              isTopLevel={isTopLevel}
            />
          );
        case MainProductType.Flight:
        case MainProductType.oneWayFlight:
          return (
            <ShoppingCartFlight
              product={product}
              showProductPrice={showProductPrice}
              mainProductType={mainProductType}
            />
          );
        case MainProductType.Room:
          return (
            <ShoppingCartRoom
              freePackage={freePackage}
              product={product}
              isCheckoutPage={isCheckoutPage}
              identifier={identifier}
              isSameDates={isSameDates}
              showProductPrice={showProductPrice}
              mainProductType={mainProductType}
              showActionButtons={
                mainProductType !== MainProductType.DynamicPackage
              }
              parentCurrencySign={resolvedParentCurrencySign}
              parentTotalPrice={parentPackageTotalPrice}
            />
          );
        case MainProductType.Hotel:
          return (
            <ShoppingCartHotel
              ProductRenderer={(props) =>
                ProductRenderer({
                  ...props,
                  topLevelProductId:
                    topLevelProductId || product?.identifier || product?.id,
                  mainProductType,
                  parentCurrencySign: resolvedParentCurrencySign,
                  parentTotalPrice: parentPackageTotalPrice,
                })
              }
              product={product}
              identifier={identifier}
              isTopLevel={isTopLevel}
              showProductPrice={showProductPrice}
              mainProductType={mainProductType}
              parentCurrencySign={resolvedParentCurrencySign}
              parentTotalPrice={parentPackageTotalPrice}
            />
          );
        case MainProductType.DynamicPackage:
        case MainProductType.Package:
          return (
            <ShoppingCartPackage
              subProducts={product.subProducts || []}
              product={product}
              ProductRenderer={(props) =>
                ProductRenderer({
                  ...props,
                  topLevelProductId:
                    topLevelProductId || product?.identifier || product?.id,
                  mainProductType,
                  parentCurrencySign: resolvedParentCurrencySign,
                  parentTotalPrice: parentPackageTotalPrice,
                })
              }
              identifier={identifier}
              showProductPrice={showProductPrice}
              mainProductType={product?.type}
              isCheckoutPage={isCheckoutPage}
            />
          );
        case MainProductType.transfer:
          return (
            <ShoppingCartTransfer
              product={product as unknown as TransferProduct}
              transfer_type={transfer_type}
              mainProductType={mainProductType}
            />
          );
        default:
          return null;
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [freePackage, isCheckoutPage, showProductPrice, shoppingCartV2?.products]
  );

  const installmentsInterestOP = useMemo(() => {
    return shoppingCartV2?.products?.find(
      (product) => product.type === MainProductType.installmentsInterest
    );
  }, [shoppingCartV2?.products]);

  const isAgent = !isEmpty(agentInfo);
  const [cashbackDetailsVisible, setCashbackDetailsVisible] = useState(false);
  const showCashbackDetails = (open: boolean) => {
    setCashbackDetailsVisible(open);
  };

  return (
    <div
      id="ShoppingCart"
      className={
        'shopping_cart ' +
        className +
        (agentInfo?.agentMode ? ' shopping_cart-agent' : '') +
        (shoppingCartOpen || isMobileView ? ' cart-open' : '')
      }
    >
      {generateCssVariables('#ShoppingCart', config?.theme)}

      {showCloseButton && (
        <AiFillCloseCircle
          size={30}
          className="close"
          onClick={() => {
            closeFromHeader && closeFromHeader();
            setShoppingCartOpen(false);
          }}
          id="btn-close-cart"
        />
      )}
      <ShoppingCartHeader
        checkout={isCheckoutPage}
        setShoppingCartOpen={setShoppingCartOpen}
        shoppingCartOpen={shoppingCartOpen || Boolean(open)}
        setAddUserModal={setAddUserModal}
        diamondsCount={
          shoppingCartV2?.total_diamonds ?? shoppingCartV2?.diamonds
        }
      />
      <AddUserModal
        addUserModal={addUserModal}
        setAddUserModal={setAddUserModal}
      />

      {(shoppingCartOpen || Boolean(open)) &&
        (shoppingCartV2?.cartNotValid ? (
          <CartNotValid />
        ) : (
          <>
            {isLocalInventory ? (
              <div className="localInventoryContainer">
                <h3>{t?.localInventoryAlert}</h3>
              </div>
            ) : !shoppingCartV2?.orderId ? (
              <ShoppingCartEmpty />
            ) : (
              <div className="content" id="obs-cart-content">
                {agentInfo.agentMode && (
                  <div id="obs-cart-agent">
                  <AgentActions
                    addUserModal={addUserModal}
                    setAddUserModal={setAddUserModal}
                    isMangeOrderPage={isMangeOrderPage}
                  />
                  </div>
                )}
                <div id="obs-cart-products-list">
                  {shoppingCartV2?.products?.map((product) => {
                    return (
                      <React.Fragment key={product.identifier}>
                        {ProductRenderer({
                          product,
                          identifier: product.identifier,
                          isTopLevel: true,
                          topLevelProductId: product.identifier || product.id,
                          mainProductType: product.type,
                        })}
                      </React.Fragment>
                    );
                  })}
                </div>

                {installmentsInterestOP?.id ? (
                  <ShoppingCartInstallmentsInterest
                    product={installmentsInterestOP}
                  />
                ) : null}

                {appliedSubsidize?.length > 0 && <ShoppingCartSubsidize />}
              </div>
            )}

            {settings?.show_coupon_code && showDiscounCodeOrIntegrationInfo && (
              <DiscountCode
                t={t}
                shoppingCart={shoppingCartV2 as any}
                pageType={pageType}
              />
            )}
            {
              <div className="footer" id="obs-cart-footer">
                <Tooltip
                  title={
                    !shoppingCartV2?.orderId ? t.shoppingCartButtonTooltip : ''
                  }
                >
                  <div
                    className="btn-style-5"
                    onClick={!isCheckoutPage ? handleCheckout : undefined}
                  >
                    {!freePackage ? (
                      <>
                        <ul className={'total-cart-pricing'}>
                          {/* {config?.siteConfig?.shareB2C?.cashback?.is_active && cashbackDetailsVisible && (
                            <CashbackDetails
                              close={() => showCashbackDetails(false)}
                              cashbackCount={shoppingCartV2?.diamonds}
                              t_wallet={t_wallet}
                            />
                          )} */}
                          {/* {
                            config?.siteConfig?.shareB2C?.cashback?.is_active && (
                              <li className={'total-agent-comm'}>
                                <div className="label">{"Total diamonds 💎"}</div>
                                <div
                                  style={{
                                    display: 'flex',
                                    gap: 10,
                                    alignItems: 'center',
                                  }}
                                >
                                  {shoppingCartV2?.diamonds || 0} ILS
                                </div>
                              </li>
                            )
                          } */}
                          {agentInfo?.agentMode &&
                            !!shoppingCartV2?.orderId && (
                              <li className={'total-agent-comm'}>
                                <div className="label">{t.totalAgentComm}</div>
                                {/* {config?.siteConfig?.shareB2C?.cashback?.is_active && (
                                  <div
                                    className="wallet-icon"
                                    onClick={(e) => {
                                      e?.stopPropagation();
                                      e?.preventDefault();
                                      showCashbackDetails(true);
                                    }}
                                  >
                                    <DiamondSVG width={24} height={24} />
                                  </div>
                                )} */}
                                <div
                                  style={{
                                    display: 'flex',
                                    gap: 10,
                                    alignItems: 'center',
                                  }}
                                >
                                  {isUpdateCommissionNeeded() &&
                                    shoppingCartV2?.orderSecret &&
                                    !shoppingCartV2?.isSaved && (
                                      // <div className="update-commission">
                                      //   <Tooltip title={t.commissionNotSaved}>
                                      //     <ButtonWithIcon
                                      //       onClick={handleUpdateCommission}
                                      //       loading={isUpdateCommissionLoading}
                                      //       // icon={<MdWarning color="yellow" />}
                                      //       icon={<FaRegSave />}
                                      //       text={t.updateCommission}
                                      //       className="btn-style-1"
                                      //     ></ButtonWithIcon>
                                      //   </Tooltip>
                                      // </div>

                                      <div className="update-commission">
                                        <Tooltip title={t.commissionNotSaved}>
                                          <Button
                                            onClick={handleUpdateCommission}
                                            loading={isUpdateCommissionLoading}
                                            className="btn-style-1"
                                          >
                                            {t.updateCommission}
                                          </Button>
                                        </Tooltip>
                                      </div>
                                    )}
                                  {/* {(
                                Number(shoppingCartV2?.total_price) -
                                shoppingCartV2?.total_net_price
                                ).toFixed(2)} */}
                                  {(
                                    Number(shoppingCartV2?.total_price) -
                                    shoppingCartV2?.total_net_price
                                  ).toFixed(2)}
                                  {String.fromCharCode(160)}
                                  {shoppingCartV2?.currency_sign}
                                </div>
                              </li>
                            )}
                          {agentInfo?.agentMode &&
                            config?.siteConfig?.shareB2C?.cashback?.is_active &&
                            (shoppingCartV2?.redeemedDiamonds?.length ?? 0) >
                              0 && (
                              <li className={'total-agent-comm redeemed-list'}>
                                <div className="redeemed-rows">
                                  {/* Subtotal (previous price before discounts) */}
                                  <div className="redeemed-row subtotal-row">
                                    <div className="row-label">
                                      {t_wallet.Subtotal}
                                    </div>
                                    <div className="row-amount subtotal-amount">
                                      {Number(
                                        shoppingCartV2?.total_price_before_discount
                                      ).toFixed(2)}{' '}
                                      {String.fromCharCode(160)}
                                      {shoppingCartV2?.currency_sign}
                                    </div>
                                  </div>

                                  {/* Each redeemed discount (one row per redeemedDiamonds item) */}
                                  {shoppingCartV2?.redeemedDiamonds?.map(
                                    (amt: number | string, i: number) => {
                                      const signed = Number(amt) || 0; // probably negative
                                      // Show as "- 50 $" (space after minus for the visual style)
                                      const display =
                                        signed < 0
                                          ? `- ${Math.abs(signed)}`
                                          : `- ${signed}`;
                                      return (
                                        <div
                                          className="redeemed-row discount-row"
                                          key={`redeemed-${i}`}
                                        >
                                          <div className="row-label">
                                            {t_wallet.discount}{' '}
                                            {t_wallet.diamonds}
                                          </div>
                                          <div className="row-amount discount-amount">
                                            {display} {String.fromCharCode(160)}
                                            ILS
                                          </div>
                                        </div>
                                      );
                                    }
                                  )}
                                </div>
                              </li>
                            )}

                          <li>
                            <div>
                              {t.totalPrice}
                              {String.fromCharCode(160)}
                              <span className="currency-sign">
                                {shoppingCartV2?.currency_sign}
                              </span>
                            </div>
                            <div>
                              {shoppingCartV2?.total_price?.toFixed(0)}
                              {String.fromCharCode(160)}
                              {shoppingCartV2?.currency_sign}
                            </div>
                          </li>
                        </ul>
                      </>
                    ) : (
                      <div className="free-package">{t.placeOrder}</div>
                    )}
                  </div>
                </Tooltip>
                {!!shoppingCartV2?.orderId &&
                !isCheckoutPage &&
                !freePackage ? (
                  <div
                    className="move-to-checkout"
                    onClick={!isCheckoutPage ? handleCheckout : undefined}
                  >
                    {t.proceedToPayment}
                  </div>
                ) : null}
              </div>
            }
          </>
        ))}
    </div>
  );
};

export default ShoppingCartV2Component;
